import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductNameById, companyNamesById } from '../common/constants';
import axe from '../services/axios-client/axiosClient';
import PageLoading from '../common/components/elements/PageLoading';

const getCompanyNameById = (id) => {
  const c = companyNamesById.find((comp) => String(comp.id) === String(id));
  return c ? c.name : '-';
};

const weekdayNames = ['DOM', 'LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB'];

function formatMonthYear(date) {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

export default function Expirations() {
  const [current, setCurrent] = useState(() => new Date());
  const [daysData, setDaysData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const year = current.getFullYear();
    const month = current.getMonth() + 1;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const resp = await axe.get('/expirations', { params: { year, month } });

        if (cancelled) return;
        const days = resp?.data?.data?.days || resp?.data?.days;

        if (days) {
          const normalized = {};
          Object.keys(days).forEach((k) => {
            normalized[Number(k)] = days[k];
          });
          setDaysData(normalized);
        } else {
          setDaysData({});
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [current]);

  const year = current.getFullYear();
  const month = current.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthOptions = Array.from({ length: 12 }, (_, m) => new Date(year, m, 1));

  function downloadCsv() {
    const rows = [
      ['Tipo Di Polizza', 'Prodotto', 'Cliente', 'Compagnia', 'Data di scadenza', 'Targa'],
    ];

    for (let d = 1; d <= daysInMonth; d++) {
      const ev = daysData[d] || {
        polizze: 0,
        semiannuals: 0,
        polizze_items: [],
        semiannuals_items: [],
      };

      ev.polizze_items.forEach((item) => {
        rows.push([
          'Annuale',
          getProductNameById(Number(item.product_id)) || item.product_id || '-',
          item.company_name || item.client_name || '-',
          item.company_id ? getCompanyNameById(item.company_id) : '-',
          item.annual_expiry_date ? item.annual_expiry_date.split(' ')[0] : '-',
          item.moto_plate || item.vehicle_plate || '-',
        ]);
      });

      ev.semiannuals_items.forEach((item) => {
        rows.push([
          'Semestrale',
          getProductNameById(Number(item.product_id)) || item.product_id || '-',
          item.company_name || item.client_name || '-',
          item.company_id ? getCompanyNameById(item.company_id) : '-',
          item.semiannual_expiry_date ? item.semiannual_expiry_date.split(' ')[0] : '-',
          item.moto_plate || item.vehicle_plate || '-',
        ]);
      });
    }

    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const monthName = current.toLocaleString('it', { month: 'long' });
    a.download = `Scadenze – ${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <PageLoading />;
  }
  return (
    <div className="container mt-4">
      <h2 className="mb-2 fs-4 fw-semibold">
        Scadenze –
        {' '}
        {formatMonthYear(current)}
      </h2>

      <div className="d-flex align-items-center mb-3 gap-3">
        <label className="d-flex flex-column">
          <span className="mb-1">Visualizza Mese</span>
          <select
            value={month}
            onChange={(e) => setCurrent(new Date(year, Number(e.target.value), 1))}
            className="form-control form-control-sm"
          >
            {monthOptions.map((m) => (
              <option key={m.getMonth()} value={m.getMonth()}>
                {formatMonthYear(m)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="btn btn-link ms-auto p-0 text-success text-decoration-none"
          onClick={downloadCsv}
          disabled={loading}
        >
          Scarica scadenze
        </button>
      </div>

      {loading && <PageLoading />}

      {!loading && error && (
        <div className="alert alert-danger">
          <strong>Errore:</strong>
          <div>{String(error)}</div>
        </div>
      )}

      {!loading && (
        <div className="list-group">
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const date = new Date(year, month, day);
            const weekday = weekdayNames[date.getDay()];

            const events = daysData[day] || { polizze: 0, semiannuals: 0 };

            const hasDetails = (events.polizze || 0) > 0 || (events.semiannuals || 0) > 0;

            const now = new Date();
            const isToday = now.getFullYear() === date.getFullYear()
              && now.getMonth() === date.getMonth()
              && now.getDate() === date.getDate();

            return (
              <div
                key={day}
                className="list-group-item list-group-item-action py-3"
                style={isToday ? { borderLeft: '4px solid #198754' } : {}}
              >
                <div className="d-flex align-items-center flex-nowrap">
                  <div className="text-center me-3 pe-3 border-end" style={{ width: 90 }}>
                    <div className={isToday ? 'small fw-bold text-success' : 'text-muted small'}>
                      {String(day).padStart(2, '0')}
                    </div>
                    <div className={isToday ? 'small fw-bold text-success' : 'small fw-bold'}>
                      {weekday}
                    </div>
                  </div>

                  <div className="flex-grow-1">
                    <div className="row align-items-center g-3">

                      <div className="col-12 col-md ps-md-5 gap-4">
                        {events.polizze > 0 && (
                          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-start">
                            <span className="text-dark">Polizze</span>
                            <span className="badge rounded-pill bg-primary-subtle text-primary">
                              {events.polizze}
                              {' '}
                              in scadenza
                            </span>
                          </div>
                        )}
                        {events.semiannuals > 0 && (
                          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-start">
                            <span className="text-dark">Rate</span>
                            <span className="badge rounded-pill bg-danger-subtle text-danger">
                              {events.semiannuals}
                              {' '}
                              in scadenza
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="ms-3 text-end">
                    <button
                      type="button"
                      className={[
                        'btn btn-link p-0 text-decoration-none',
                        hasDetails ? 'text-success' : 'text-muted',
                      ].join(' ')}
                      disabled={!hasDetails}
                      title={!hasDetails ? 'Nessuna scadenza per questa data' : ''}
                      onClick={() => {
                        if (!hasDetails) return;

                        const params = new URLSearchParams({
                          day: String(day),
                          month: String(current.getMonth() + 1),
                          year: String(current.getFullYear()),
                        });

                        navigate(`/expirations-details?${params.toString()}`, { state: { summary: events } });
                      }}
                    >
                      Dettagli &gt;
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
